import { Close, CloseFullscreen, OpenInFull, WarningAmberOutlined } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    GlobalStyles,
    IconButton,
    Stack,
    Switch,
    ThemeProvider,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useMemo, useState } from 'react';

import { hasGoodContrast } from 'config/defaultBranding';
import { getMandatoryPlaceholdersForType, getPlaceholdersForType } from 'config/placeholders';
import useTranslate from 'hooks/useTranslate';
import appThemeV2 from 'theme/themeV2';
import type { Milestone, PlaceholderDefinition, Template, TemplateType } from 'types';
import {
    buildDocumentContentCss,
    normalizeFontFamilyStyles,
    quoteFontFamily,
} from 'views/retainerAgreement/components/editor/documentContentStyles';
import 'views/retainerAgreement/components/editor/listStyles.css';
import {
    calculateTotalFee,
    formatCurrency,
    generateMilestoneTableHTML,
} from 'views/retainerAgreement/pricingUtils';

const TYPE_LABEL_KEYS: Record<string, string> = {
    'visa-application': 'templateTypeVisaApplication',
    'service-agreement': 'templateTypeServiceAgreement',
    eligibility: 'templateTypeEligibility',
    milestones: 'templateTypeMilestones',
    engagement: 'templateTypeEngagement',
    referral: 'templateTypeReferral',
    subcontractor: 'templateTypeSubcontractor',
    msa: 'templateTypeMsa',
    'revenue-sharing': 'templateTypeRevenueSharing',
    nda: 'templateTypeNda',
    'preferred-partner': 'templateTypePreferredPartner',
};

interface TemplatePreviewDialogProps {
    open: boolean;
    template: Template | null;
    onClose: () => void;
    onEditBranding?: () => void;
}

const logoMaxHeight = (size: string | undefined, footer: boolean) => {
    if (footer) {
        if (size === 'small') return '40px';
        if (size === 'medium') return '60px';
        return '80px';
    }
    if (size === 'small') return '60px';
    if (size === 'medium') return '100px';
    return '140px';
};

const replacePlaceholders = (template: Template, html: string, useSampleData: boolean) => {
    if (!useSampleData) return html;

    let result = html;

    if (template.pricingConfig) {
        const totalFee = calculateTotalFee(template.pricingConfig);
        const currency = template.pricingConfig.currency;

        result = result.replace(/\{\{payment\.total_fee\}\}/g, formatCurrency(totalFee, currency));
        result = result.replace(/\{\{payment\.currency\}\}/g, currency);

        if (template.pricingConfig.model === 'hourly' && template.pricingConfig.hourly) {
            result = result.replace(
                /\{\{payment\.hourly_rate\}\}/g,
                formatCurrency(template.pricingConfig.hourly.hourlyRate, currency)
            );
            result = result.replace(
                /\{\{payment\.estimated_hours\}\}/g,
                template.pricingConfig.hourly.estimatedHours.toString()
            );
            result = result.replace(
                /\{\{payment\.estimated_total\}\}/g,
                formatCurrency(totalFee, currency)
            );
        }

        if (template.pricingConfig.model === 'hybrid' && template.pricingConfig.hybrid) {
            result = result.replace(
                /\{\{payment\.upfront_fee\}\}/g,
                formatCurrency(template.pricingConfig.hybrid.upfrontFee, currency)
            );
            const milestoneTotal = template.pricingConfig.hybrid.milestones.reduce(
                (sum: number, milestone: Milestone) => sum + (milestone.amount || 0),
                0
            );
            result = result.replace(
                /\{\{payment\.milestone_total\}\}/g,
                formatCurrency(milestoneTotal, currency)
            );
            result = result.replace(
                /\{\{retainer\.milestones_table\}\}/g,
                generateMilestoneTableHTML(
                    template.pricingConfig.hybrid.milestones,
                    currency,
                    template.pricingConfig.hybrid.displayMode,
                    template.pricingConfig.hybrid.upfrontFee
                )
            );
        }

        if (template.pricingConfig.model === 'milestone' && template.pricingConfig.milestone) {
            result = result.replace(
                /\{\{retainer\.milestones_table\}\}/g,
                generateMilestoneTableHTML(
                    template.pricingConfig.milestone.milestones,
                    currency,
                    template.pricingConfig.milestone.displayMode,
                    template.pricingConfig.milestone.totalFee
                )
            );
        }
    }

    getPlaceholdersForType(template.type).forEach((placeholder: PlaceholderDefinition) => {
        const regex = new RegExp(placeholder.token.replace(/[{}]/g, '\\$&'), 'g');
        result = result.replace(
            regex,
            `<span class="placeholder-value">${placeholder.sampleValue}</span>`
        );
    });

    return result;
};

const TemplatePreviewDialogBody = ({
    template,
    onClose,
    onEditBranding,
}: {
    template: Template;
    onClose: () => void;
    onEditBranding?: () => void;
}) => {
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [expanded, setExpanded] = useState(false);
    const [useSampleData, setUseSampleData] = useState(true);
    const fullScreen = isMobile || expanded;

    const content = template.htmlContent || template.content;
    const processedContent = useMemo(
        () => normalizeFontFamilyStyles(replacePlaceholders(template, content, useSampleData)),
        [template, content, useSampleData]
    );

    const missingPlaceholders = getMandatoryPlaceholdersForType(template.type)
        .filter((placeholder: PlaceholderDefinition) => !content.includes(placeholder.token))
        .map((placeholder: PlaceholderDefinition) => placeholder.label);
    const hasLowContrast = template.branding
        ? !hasGoodContrast(template.branding.textColor, template.branding.backgroundColor)
        : false;
    const typeLabelKey = TYPE_LABEL_KEYS[template.type as TemplateType];
    const branding = template.branding;

    return (
        <Dialog
            open
            onClose={onClose}
            fullWidth
            maxWidth="xl"
            fullScreen={fullScreen}
            aria-labelledby="template-preview-title"
            slotProps={{
                paper: {
                    sx: {
                        height: fullScreen ? '100%' : '88vh',
                        maxHeight: fullScreen ? '100%' : '88vh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        borderRadius: fullScreen ? 0 : 2,
                    },
                },
            }}
        >
            <GlobalStyles styles={buildDocumentContentCss('.template-preview-content', branding)} />
            <GlobalStyles
                styles={{
                    '.template-preview-content .placeholder-value': {
                        backgroundColor: '#fef3c7',
                        padding: '2px 4px',
                        borderRadius: '2px',
                        fontWeight: 500,
                    },
                }}
            />
            <DialogTitle
                component="div"
                id="template-preview-title"
                sx={{
                    px: { xs: 1.5, sm: 2.5 },
                    py: 1.5,
                    borderBottom: 1,
                    borderColor: 'divider',
                    flexShrink: 0,
                }}
            >
                <Stack
                    direction="row"
                    spacing={1.5}
                    sx={{ alignItems: 'center', justifyContent: 'space-between', minWidth: 0 }}
                >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                            variant="subtitle1"
                            component="h2"
                            noWrap
                            sx={{ fontWeight: 700, lineHeight: 1.3 }}
                        >
                            {template.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {typeLabelKey ? translate(typeLabelKey) : translate('previewTemplate')}
                        </Typography>
                    </Box>
                    <Stack
                        direction="row"
                        spacing={0.75}
                        sx={{ alignItems: 'center', flexShrink: 0 }}
                    >
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={useSampleData}
                                    onChange={() => setUseSampleData((value) => !value)}
                                />
                            }
                            label={isMobile ? '' : translate('showSampleData')}
                            sx={{
                                mr: 0.5,
                                '& .MuiFormControlLabel-label': {
                                    fontSize: 13,
                                    fontWeight: 600,
                                },
                            }}
                        />
                        {onEditBranding ? (
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={onEditBranding}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    display: { xs: 'none', sm: 'inline-flex' },
                                }}
                            >
                                {translate('edit')}
                            </Button>
                        ) : null}
                        {isMobile ? null : (
                            <Tooltip
                                title={
                                    expanded
                                        ? translate('exitFullScreen')
                                        : translate('viewFullScreen')
                                }
                            >
                                <IconButton
                                    size="small"
                                    onClick={() => setExpanded((value) => !value)}
                                    aria-label={
                                        expanded
                                            ? translate('exitFullScreen')
                                            : translate('viewFullScreen')
                                    }
                                >
                                    {expanded ? (
                                        <CloseFullscreen fontSize="small" />
                                    ) : (
                                        <OpenInFull fontSize="small" />
                                    )}
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title={translate('close')}>
                            <IconButton
                                size="small"
                                onClick={onClose}
                                aria-label={translate('close')}
                            >
                                <Close fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
            </DialogTitle>
            <DialogContent
                sx={{
                    p: 0,
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    backgroundColor: 'grey.50',
                }}
            >
                {missingPlaceholders.length > 0 || !template.hasLogo || hasLowContrast ? (
                    <Stack spacing={1} sx={{ px: { xs: 1.5, md: 3 }, pt: 2 }}>
                        {missingPlaceholders.length > 0 ? (
                            <Alert
                                severity="error"
                                icon={<WarningAmberOutlined fontSize="small" />}
                            >
                                {translate('missingMandatoryPlaceholders')}:{' '}
                                {missingPlaceholders.join(', ')}
                            </Alert>
                        ) : null}
                        {!template.hasLogo || hasLowContrast ? (
                            <Alert
                                severity="warning"
                                icon={<WarningAmberOutlined fontSize="small" />}
                            >
                                {!template.hasLogo
                                    ? `${translate('templatePreviewNoLogo')} `
                                    : null}
                                {hasLowContrast ? translate('templatePreviewLowContrast') : null}
                            </Alert>
                        ) : null}
                    </Stack>
                ) : null}
                <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: { xs: 1.5, md: 3 } }}>
                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: fullScreen ? 960 : 816,
                            minHeight: { xs: 'auto', md: 1056 },
                            mx: 'auto',
                            bgcolor: branding?.backgroundColor || 'background.paper',
                            color: branding?.textColor || 'text.primary',
                            boxShadow: 1,
                            borderRadius: 1,
                            fontFamily: branding ? quoteFontFamily(branding.fontFamily) : undefined,
                            fontSize: branding ? `${branding.bodyFontSize}pt` : undefined,
                            lineHeight: branding?.lineHeight,
                            ...(branding
                                ? {
                                      pt: { xs: 2, md: `${branding.marginTop}in` },
                                      pr: { xs: 2, md: `${branding.marginRight}in` },
                                      pb: { xs: 3, md: `${branding.marginBottom}in` },
                                      pl: { xs: 2, md: `${branding.marginLeft}in` },
                                  }
                                : { px: { xs: 2, md: 5 }, py: { xs: 2, md: 5 } }),
                        }}
                    >
                        {branding?.headerLogo ? (
                            <Box
                                sx={{
                                    textAlign: branding.headerLogo.position,
                                    mb: 2.5,
                                }}
                            >
                                <Box
                                    component="img"
                                    src={branding.headerLogo.url}
                                    alt=""
                                    sx={{
                                        maxHeight: logoMaxHeight(branding.headerLogo.size, false),
                                        display:
                                            branding.headerLogo.position === 'center'
                                                ? 'inline-block'
                                                : 'block',
                                    }}
                                />
                            </Box>
                        ) : null}
                        <Box
                            className="template-preview-content"
                            dangerouslySetInnerHTML={{ __html: processedContent }}
                        />
                        {branding?.footerLogo ? (
                            <Box
                                sx={{
                                    textAlign: branding.footerLogo.position,
                                    mt: 2.5,
                                }}
                            >
                                <Box
                                    component="img"
                                    src={branding.footerLogo.url}
                                    alt=""
                                    sx={{
                                        maxHeight: logoMaxHeight(branding.footerLogo.size, true),
                                        display:
                                            branding.footerLogo.position === 'center'
                                                ? 'inline-block'
                                                : 'block',
                                    }}
                                />
                            </Box>
                        ) : null}
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export function TemplatePreviewDialog({
    open,
    template,
    onClose,
    onEditBranding,
}: TemplatePreviewDialogProps) {
    if (!open || !template) return null;

    return (
        <ThemeProvider theme={appThemeV2}>
            <TemplatePreviewDialogBody
                key={template.id}
                template={template}
                onClose={onClose}
                onEditBranding={onEditBranding}
            />
        </ThemeProvider>
    );
}
